import React, { useEffect, useState } from 'react';
import { Theme, useMediaQuery, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Button,
  Box,
  ContentTemplate,
  ErrorInfo,
  Grid,
  Icon,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { useParams, Prompt } from 'react-router-dom';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { MutationStatus } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import {
  AccessArrangmentGetMedicalConditionsResponse,
  AccessArrangmentGetSelectedResponse,
  MedicalCondition,
  MedicalConditionAmendment,
} from '../../../../../../api/candidate/access-arrangement/model';
import {attachedFilesService, candidateService, MenuItem} from '../../../../../../api';
import SubmittedDetails, { SubmittedDetailsProps } from '../SubmittedDetails';
import { prepareFiles } from '../../../../../../utils/UploadUtil';
import useSetUploadButtonStatus from '../../../../../../hooks/useSetUploadButtonStatus';
import DraftTimeStamp from '../../../../../../components/DraftTimeStamp';
import { ApplicationType } from '../../../../../../api/candidate/config/model';
import UploadDocuments from '../../../../../../pages/accessArrangement/SelectCondition/components/UploadDocuments';
import OthersTextArea from '../../../../../../pages/accessArrangement/SelectCondition/components/OthersTextArea';
import SelectMenu from '../../../../../../pages/accessArrangement/SelectCondition/components/SelectMenu';
import { useGetMedicalCondition } from '../../../../../../pages/accessArrangement/SelectCondition/logic/useGetMedicalCondition';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  buttonContainer: {
    marginTop: theme.spacing(8),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  medicalConditionSelect: {
    marginBottom: theme.spacing(6),
  },
  iconReminder: {
    marginRight: theme.spacing(1),
  },
  messageReminder: {
    fontWeight: 700,
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

interface SelectConditionContentProps {
  amendment: boolean;
  handleBack: () => void;
  onSuccess: () => void;
}

const maxCharacterCount = 200;

export enum FormIds {
  MEDICAL_CONDITION_SELECT_MENU = 'medical-condition-select-menu',
  OTHERS_TEXT_AREA_OVER_LIMIT = 'others-text-area-over-limit',
  OTHERS_TEXT_AREA = 'others-text-area',
  UPLOAD_DOCUMENTS_CONTAINER = 'upload-document-container',
}

export interface ErrorObject {
  id: FormIds;
  text: string;
}

const findError = (errorId: string, formErrors: ErrorObject[]) =>
  formErrors.find((formError) => formError.id === errorId);

const setNewFiles = (data: any, files: any, setFiles: any, setShouldSubmit: any) => {
  const newFiles = [];
  for (let i = 0; i < files.length; i += 1) {
    const pendingFile = files[i];
    const fileData = data[i];
    pendingFile.presignedUrl = fileData.presignedUrl;
    pendingFile.key = fileData.key;
    newFiles.push(pendingFile);
  }
  setFiles(newFiles);

  setShouldSubmit(true);
};

const SelectConditionPage = ({ amendment, handleBack, onSuccess }: SelectConditionContentProps) => {
  const { t } = useTranslation();
  const [isOthersSelected, setIsOthersSelected] = useState<boolean>(false);
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [showUploadDocuments, setShowUploadDocuments] = useState(amendment);
  const [selectedMedicalCondition, setSelectedMedicalCondition] = useState<MenuItem[]>([]);
  const [othersString, setOthersString] = useState<string>('');
  const [formErrors, setFormErrors] = useState<ErrorObject[]>([]);
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { uuid } = useParams<{ uuid: string }>();
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [submittedData, setSubmittedData] = useState<SubmittedDetailsProps>();
  const [submittedFiles, setSubmittedFiles] = useState(0);
  const [dropdownReady, setDropdownReady] = useState(false);
  const [allowProceed, setAllowProceed] = useState(false);
  const {
    data: timestampData,
    error: timestampError,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementDraftDateTime(uuid);

  const setDraftData = (
    medicalConditionDraftModel: AccessArrangmentGetSelectedResponse,
    medicalConditionDropdown: AccessArrangmentGetMedicalConditionsResponse
  ) => {
    if (medicalConditionDraftModel.medicalConditionDropdownModel.remarks) {
      setOthersString(medicalConditionDraftModel.medicalConditionDropdownModel.remarks);
    }

    if (medicalConditionDraftModel.medicalConditionDropdownModel.medicalConditions) {
      const draftMedicalConditionData =
        medicalConditionDraftModel.medicalConditionDropdownModel.medicalConditions;
      const selectedMedicalConditionsIndex: MenuItem[] = [];

      draftMedicalConditionData.forEach((medicalCondition) => {
        const foundIndex = medicalConditionDropdown.find(
          (condition) => condition.name === medicalCondition.name
        );
        if (foundIndex) {
          const item = {
            key: foundIndex.uuid,
            value: foundIndex.uuid,
            children: foundIndex.name
          }
          selectedMedicalConditionsIndex.push(item);
        }
      });

      setSelectedMedicalCondition(selectedMedicalConditionsIndex);
    }

    if (medicalConditionDraftModel.fileAttachedInfoModel) {
      const newFiles: UploadedFile[] = medicalConditionDraftModel.fileAttachedInfoModel.map(
        (draftFile) => {
          const newFile = new File(
            [new ArrayBuffer(draftFile.fileSizeInBytes)],
            draftFile.fileName
          );
          return {
            file: newFile,
            hasError: false,
            pending: false,
            uploaded: true,
            key: draftFile.fileUuid,
          };
        }
      );
      setDraftFiles(newFiles);
    }
  };

  const setData = (
    medicalConditionDraftModel: MedicalConditionAmendment | AccessArrangmentGetSelectedResponse,
    medicalConditionDropdown: AccessArrangmentGetMedicalConditionsResponse
  ) => {
    if (amendment) {
      const data = medicalConditionDraftModel as MedicalConditionAmendment;
      setSubmittedFiles(data.medicalConditionSubmittedModel.fileAttachedInfoModel.length);

      setSubmittedData({
        medicalConditions:
          data.medicalConditionSubmittedModel.medicalConditionDropdownModel.medicalConditions,
        reason: data.medicalConditionSubmittedModel.medicalConditionDropdownModel.remarks,
        documents: data.medicalConditionSubmittedModel.fileAttachedInfoModel,
      });

      setDraftData(data.medicalConditionDraftModel, medicalConditionDropdown);
    } else {
      setDraftData(
        medicalConditionDraftModel as AccessArrangmentGetSelectedResponse,
        medicalConditionDropdown
      );
    }
  };

  const { medicalConditionData, error } = useGetMedicalCondition(
    uuid,
    setData,
    setDropdownReady,
    amendment
  );

  const postRequestS3PresignLinks = candidateService.accessArrangement.candidate.usePostAppealArrangementUploadDocuments(
    ({ data }) => {
      setNewFiles(data, files, setFiles, setShouldSubmit);
      const newFiles: React.SetStateAction<UploadedFile[]> = [];
      files.forEach((_item, index) => {
        const pendingFile = files[index];
        const fileData = data[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        newFiles.push(pendingFile);
      });

      setFiles(newFiles);

      setShouldSubmit(true);
    },
    (err) => {
      setApiButtonStatus('error');
      setErrorMsg(err?.response?.data.message);
    }
  );
  const postSaveRequest = candidateService.accessArrangement.candidate.usePostAppealArrangementModifySelectedMedicalConditions(
    () => {
      if (files?.length > 0) {
        const { filenames } = prepareFiles(files, setFiles);
        sessionStorage.removeItem(uuid);

        postRequestS3PresignLinks.mutate({
          payload: {
            applicationType: 'ACCESS_ARRANGEMENT',
            uuid,
            filenames,
          },
          uuid,
        });
      } else {
        if (
          amendment &&
          selectedMedicalCondition.length === 0 &&
          files.length < 1 &&
          draftFiles.length < 1
        ) {
          sessionStorage.setItem(uuid, 'noAmendment');
        }
        if (amendment && files.length > 0 && draftFiles.length > 0) {
          sessionStorage.removeItem(uuid);
        }
        onSuccess();
      }
    },
    (err) => {
      setApiButtonStatus('error');
      setErrorMsg(err?.response?.data.message);
    }
  );

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: () => onSuccess,
  });

  const { mutate: deleteFileMutate } = attachedFilesService.all.useDeleteAppealArrangementFile(
    () => null
  );

  useEffect(() => {
    const isUploading = files.some((file) => file.pending);
    const hasError = files.some((file) => file.hasError);
    switch (true) {
      case isUploading:
        setApiButtonStatus('loading');
        break;
      case hasError:
        setShouldSubmit(false);
        setApiButtonStatus('idle');
        break;
      case files.length > 0 && !isUploading && shouldSubmit:
        onSuccess();
        break;
      default:
        setApiButtonStatus('idle');
        break;
    }
  }, [files, shouldSubmit, onSuccess]);

  useEffect(() => {
    const newErrors: ErrorObject[] = [];
    const charactersRemaining = maxCharacterCount - othersString.length;
    if (!amendment && selectedMedicalCondition.length < 1) {
      newErrors.push({
        id: FormIds.MEDICAL_CONDITION_SELECT_MENU,
        text: t('accessArrangement->>SelectCondition.MedicalConditionsEmptyError'),
      });
    } else {
      if (charactersRemaining < 0) {
        newErrors.push({
          id: FormIds.OTHERS_TEXT_AREA_OVER_LIMIT,
          text:
            t('accessArrangement->>SelectCondition.OthersStringExccedError1') +
            /* Turn negative numbber to positive */
            (0 - charactersRemaining) +
            t('accessArrangement->>SelectCondition.OthersStringExccedError2'),
        });
      }
      if (!othersString && isOthersSelected) {
        newErrors.push({
          id: FormIds.OTHERS_TEXT_AREA,
          text: t('accessArrangement->>SelectCondition.OthersStringEmptyError'),
        });
      }
    }
    setFormErrors(newErrors);
  }, [othersString, files, draftFiles, selectedMedicalCondition, t, isOthersSelected, amendment]);

  useEffect(() => {
    if (!showUploadDocuments) {
      files.forEach((file) => {
        if (file.key) {
          deleteFileMutate(file.key);
        }
      });

      setFiles([]);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [deleteFileMutate, showUploadDocuments]);

   useEffect(() => {
     const handleBeforeUnload = (event: BeforeUnloadEvent) => {
       event.preventDefault();
       event.returnValue = '';
     };

     window.addEventListener('beforeunload', handleBeforeUnload);

     return () => {
       window.removeEventListener('beforeunload', handleBeforeUnload);
     };
   }, []);

  const handleUpdateDraft = () => {
    if (medicalConditionData) {
      const conditionsToSend: MedicalCondition[] = [];

      selectedMedicalCondition.forEach((selectedIndex) => {
        const medicalCondition = medicalConditionData.find(
          (condition) => condition.uuid === selectedIndex.value
        );

        if (medicalCondition) {
          conditionsToSend.push(medicalCondition);
        }
      });

      if (conditionsToSend) {
        setErrorMsg('');
        postSaveRequest.mutate({
          payload: {
            medicalConditions: conditionsToSend,
            remarks: othersString,
          },
          uuid,
        });
      }
    }
  };

  const handleSetMedicalConditions = (newMedicalConditionArray: MenuItem[]) => {
    setSelectedMedicalCondition(newMedicalConditionArray);
    if (newMedicalConditionArray.length > 0) {
      setDisplayErrors(false);
    }
  };

  const handleConfirm = () => {
    if (!amendment && files.length < 1 && draftFiles.length < 1) {
      setDisplayErrors(true);
      return;
    }
    if (
      amendment &&
      selectedMedicalCondition.length !== 0 &&
      files.length < 1 &&
      draftFiles.length < 1
    ) {
      setDisplayErrors(true);
      return;
    }
    if (formErrors.length < 1) {
      setAllowProceed(true);
      handleUpdateDraft();
    } else {
      const idToScroll = formErrors[0].id;
      if (idToScroll) {
        document.getElementById(idToScroll)?.scrollIntoView({ behavior: 'smooth' });
      }
      setDisplayErrors(true);
    }
  };

  const handleOnOtherStringChange = (newString: string) => {
    setOthersString(newString);
  };

  if (error || !medicalConditionData || timestampError) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const selectMedicalConditionError = findError(FormIds.MEDICAL_CONDITION_SELECT_MENU, formErrors);
  const uploadDocumentsContainerError = findError(FormIds.UPLOAD_DOCUMENTS_CONTAINER, formErrors);

  return (
    <>
    <Prompt
      when={!allowProceed}
      message={() => t("accessArrangement->>AccessArrangements.ReminderToSubmitPopUp")}
    />
      <ContentTemplate isDividerBottom={amendment || showUploadDocuments} isContinuable>
        <Grid className={amendment ? '' : classes.medicalConditionSelect}>
          <SectionHeader title={t('accessArrangement->>SelectCondition.MedicalConditions')} />
          {dropdownReady && (
            <SelectMenu
              selectedMedicalCondition={selectedMedicalCondition}
              onSelectMedicalCondition={handleSetMedicalConditions}
              setShowUploadDocuments={setShowUploadDocuments}
              setIsOthersSelected={setIsOthersSelected}
              medicalConditions={medicalConditionData}
              showError={displayErrors}
              error={selectMedicalConditionError}
              amendment={amendment}
            />
          )}

          {isOthersSelected && (
            <OthersTextArea
              displayError={displayErrors}
              formErrors={formErrors}
              maxCharacterCount={maxCharacterCount}
              othersString={othersString}
              setOthersString={handleOnOtherStringChange}
            />
          )}

          {amendment && submittedData && (
            <SubmittedDetails
              medicalConditions={submittedData.medicalConditions}
              reason={submittedData.reason}
              documents={submittedData.documents}
            />
          )}
        </Grid>
      </ContentTemplate>
      <ContentTemplate continuous>
        {showUploadDocuments && (
          <UploadDocuments
            isHeader
            files={files}
            setFiles={setFiles}
            hasError={uploadDocumentsContainerError && displayErrors}
            submittedFiles={submittedFiles}
            applicationType={ApplicationType.ACCESS_ARRANGEMENT}
            draftFiles={draftFiles}
            setDraftFiles={setDraftFiles}
          />
        )}
        <ErrorInfo label={errorMsg} />
        <Grid xs={12} className={showUploadDocuments ? classes.buttonContainer : ''}>
          {displayErrors &&
            selectedMedicalCondition.length > 0 &&
            files.length < 1 &&
            draftFiles.length < 1 && (
              <Typography variant="body2" className={classes.errString}>
                {t('accessArrangement->>SelectCondition.NoDocumentErrMessage')}
              </Typography>
            )}
          <Button
            onClick={() => {
              handleBack();
            }}
            variant="outlined"
            className={classes.backButton}
            fullWidth={isMobile}
          >
            {t('accessArrangement->>SelectCondition.Back')}
          </Button>
          <ApiButton
            id="confirm-medical-condition-button"
            status={apiButtonStatus}
            variant="contained"
            fullWidth={isMobile}
            onClick={handleConfirm}
          >
            {t('accessArrangement->>SelectCondition.Confirm')}
          </ApiButton>
        </Grid>
        {timestampData && <DraftTimeStamp savedAt={timestampData} />}
       <Box display="flex" alignItems="center">
         <Icon
           size="M"
           name={IconName.info}
           className={classes.iconReminder}
           color={theme.color.icon.iconTrash}
         />
         <Typography className={classes.messageReminder} style={{color: theme.color.icon.iconTrash}}>
           {t('components->>ApplicationReminderMessage.ReminderText')}
         </Typography>
       </Box>
      </ContentTemplate>
    </>
  );
};

export default SelectConditionPage;
